<script lang="ts" setup>
import IconCloseCircle from '@/assets/icons/close-circle.svg?component'
import Button from '@/components/Button.vue'
import { usePageOverlay } from '@/lib/pageOverlay'
import { onBeforeMount, onBeforeUnmount } from 'vue'

withDefaults(
  defineProps<{
    title?: string
    headingLevel?: string
    hasCloseButton?: boolean
    wide?: boolean
  }>(),
  {
    title: undefined,
    headingLevel: 'h5',
    hasCloseButton: true,
    wide: false,
  },
)

const emit = defineEmits<{
  (e: 'close'): void
}>()

const { show, hide } = usePageOverlay(
  (e) => {
    if (
      e.target instanceof Element &&
      e.target.classList.contains('PageOverlay')
    ) {
      emit('close')
    }
  },
  (e) => {
    if (e.key === 'Escape') {
      emit('close')
    }
  },
)

onBeforeMount(show)
onBeforeUnmount(hide)
</script>

<template>
  <transition name="Modal" appear>
    <div
      class="Modal"
      :class="{ 'Modal--wide': wide }"
      @click.self="emit('close')"
    >
      <div class="Modal__Outer">
        <slot name="outer"></slot>
        <div class="Modal__Inner">
          <div v-if="title" class="Modal__Title">
            <h2 :class="headingLevel">{{ title }}</h2>
          </div>
          <div class="Modal__Content">
            <slot></slot>
          </div>
          <Button
            v-if="hasCloseButton"
            class="Modal__Close"
            :modifiers="['inverted']"
            :link="true"
            @click="$emit('close')"
          >
            <template #iconBefore>
              <IconCloseCircle width="24" height="24" />
            </template>
          </Button>
        </div>
      </div>
    </div>
  </transition>
</template>

<style>
.Modal {
  position: fixed;
  inset: 0;
  z-index: 100;
  overscroll-behavior: none;
}

.Modal__Outer {
  position: fixed;
  bottom: 0;
  left: 50%;
  z-index: 21;
  transform: translate(-50%, 0);
  width: 100%;
  height: auto;
  max-height: 90%;
  background-color: var(--color-white);
  box-shadow: 0 0 20px 0 var(--color-shadow-30);
  transition: opacity 0.2s ease-in;
  display: flex;
  flex-direction: column;
}

.Modal__Inner {
  overflow-y: auto;
  overscroll-behavior: none;
  overscroll-behavior-y: none;
  padding: 24px;
}

.Modal__Close {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 20px;
  right: 20px;
  border: 0;
  width: 24px;
  height: 24px;
  border-radius: 28px;
  transition: background-color var(--global-transition);
  z-index: 1;
  color: var(--color-forest-100);

  .Button__Icon {
    width: 24px;
    height: 24px;
  }

  & svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    background-color: var(--color-forest-100);
    cursor: pointer;
  }
}

.Modal__Title {
  padding: 0 72px 0 0;
  color: var(--color-forest-100);
  font-family: var(--font-family-domaine);
  font-size: 26px;
  font-style: normal;
  font-weight: 400;
  line-height: 36px;

  & h2 {
    margin-bottom: 8px;
  }
}

.Modal__Panel {
  background-color: var(--color-lichen-100);
  margin: var(--grid-gap) calc(0px - var(--grid-gap))
    calc(0px - var(--grid-gap));
  padding: var(--grid-gap);
}

.Modal-enter-active,
.Modal-leave-active {
  transition: opacity 0.2s ease-in;

  & .Modal__Outer {
    transition: transform 0.2s ease-in;
  }
}

.Modal-enter-from,
.Modal-leave-to {
  opacity: 0;
  visibility: hidden;

  & .Modal__Outer {
    transform: translate(-50%, 110%);
  }
}

.Modal-enter-to,
.Modal-leave-from {
  opacity: 1;
  visibility: visible;

  & .Modal__Outer {
    transform: translate(-50%, 0);
  }
}

@media (--mq-landscape-tablet-and-up) {
  .Modal__Outer {
    max-width: 512px;
    border-radius: var(--radius-large);
    bottom: auto;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .Modal.Modal--wide .Modal__Outer {
    max-width: 640px;
  }

  .Modal-enter-active,
  .Modal-leave-active {
    & .Modal__Outer {
      transition: none;
    }
  }

  .Modal-enter-from,
  .Modal-leave-to {
    & .Modal__Outer {
      transform: translate(-50%, -50%);
    }
  }

  .Modal-enter-to,
  .Modal-leave-from {
    & .Modal__Outer {
      transform: translate(-50%, -50%);
    }
  }
}
</style>
